'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useUserData } from '../components/UserDataContext';
import { LoginPromptModal } from '../components/LoginPromptModal';

interface AuthGateValue {
  /** True when a Firebase user is signed in. */
  isAuthed: boolean;
  /**
   * Guard an action behind authentication.
   * - If already signed in → returns `true` (caller proceeds immediately).
   * - If signed out → opens the "create account / log in" prompt, stashes the
   *   optional `onAuthed` callback to run once the visitor authenticates, and
   *   returns `false` (caller should bail out).
   */
  requireAuth: (onAuthed?: () => void, label?: string) => boolean;
  /** Open the auth prompt directly (e.g. the 10-yap preview wall CTA). */
  openAuthPrompt: (label?: string) => void;
}

// Default (no provider mounted): behave as authed so components used outside the
// provider never crash or block — the provider is mounted app-wide in main.tsx.
const AuthGateContext = createContext<AuthGateValue>({
  isAuthed: true,
  requireAuth: () => true,
  openAuthPrompt: () => {},
});

export function AuthGateProvider({ children }: { children: ReactNode }) {
  const { firebaseUser, profileSettled } = useUserData();
  const isAuthed = !!firebaseUser;

  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState<string | undefined>(undefined);
  const pendingAction = useRef<(() => void) | null>(null);

  const openAuthPrompt = useCallback((actionLabel?: string) => {
    setLabel(actionLabel);
    setOpen(true);
  }, []);

  const requireAuth = useCallback((onAuthed?: () => void, actionLabel?: string) => {
    if (firebaseUser) return true;
    pendingAction.current = onAuthed || null;
    setLabel(actionLabel);
    setOpen(true);
    return false;
  }, [firebaseUser]);

  // Once the visitor authenticates while the prompt is open, close it and run any
  // stashed action — but only after the profile has settled, so actions that read
  // userProfile don't race the empty default profile.
  useEffect(() => {
    if (!open || !firebaseUser || !profileSettled) return;
    const action = pendingAction.current;
    pendingAction.current = null;
    setOpen(false);
    setLabel(undefined);
    if (action) {
      // Defer so context consumers re-render in the authed state first.
      setTimeout(() => { try { action(); } catch { /* noop */ } }, 0);
    }
  }, [open, firebaseUser, profileSettled]);

  const close = useCallback(() => {
    pendingAction.current = null;
    setOpen(false);
    setLabel(undefined);
  }, []);

  return (
    <AuthGateContext.Provider value={{ isAuthed, requireAuth, openAuthPrompt }}>
      {children}
      <LoginPromptModal open={open} label={label} onClose={close} />
    </AuthGateContext.Provider>
  );
}

export function useAuthGate() {
  return useContext(AuthGateContext);
}
